/**
 * Geometry `StaticWorld` builds but has no reason to own: the wreck a destroyed prop leaves, and
 * the marks painted on the ground (a marked minefield's furrows and stakes, the X at the pickup).
 * Split out when StaticWorld reached its line limit; both are pure builders, easy to test alone.
 */

import { collidersFor, quarterTurned } from '../../sim/props';
import type { Pcg32 } from '../../sim/rng';
import type { MissionDef, PropDef } from '../../sim/types';
import { mineFieldTell, pickupMark } from '../kit/groundTells';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import { brokenSpan, type SpanSite } from '../kit/spans';
import { buildWreck, wreckTurnsWithProp } from '../kit/wrecks';
import type { Palette } from '../palette';
import { propScale } from './propModels';

/**
 * Wreckage for a destroyed prop, already placed in the world at `y`: the mission targets and the
 * emplacements get their own recognisable wreck (kit/wrecks.ts), everything else generic scorched
 * rubble over the footprint the sim collided with.
 *
 * The bridge is the one RUN kind with a wreck, and the only one that needs to know about its site:
 * its broken span is built to the prop's `length`, in the prop's own frame, and canted into whatever
 * lies under the deck (`site`). It is not in the `wrecks.ts` tables because nothing there takes
 * either of those, and the span is the only run that ever falls down.
 */
export function wreckModel(def: PropDef, palette: Palette, rng: Pcg32, y: number, site: SpanSite): MeshBuilder {
  if (def.kind === 'bridge') {
    const span = brokenSpan(palette, rng, def.length ?? 4, site);
    return new MeshBuilder().merge(span, place(def.x, y, def.z, def.yaw, propScale(def)));
  }
  const box = collidersFor(def)[0];
  const turns = wreckTurnsWithProp(def.kind);
  // The collider is world-aligned - a quarter-turned run's box has its extents swapped (props.ts) - while a wreck
  // that turns with its prop is built in the prop's own frame, so it takes them the other way round.
  const unswap = turns && quarterTurned(def.yaw);
  const wx = box ? (box.shape === 'circle' ? box.r : box.hx) : 1.5;
  const wz = box ? (box.shape === 'circle' ? box.r : box.hz) : 1.5;
  const halfX = unswap ? wz : wx;
  const halfZ = unswap ? wx : wz;
  // Its own wreck is modelled in the prop's frame; generic rubble fills the axis-aligned collider.
  const yaw = turns ? def.yaw : 0;
  const model = buildWreck(def.kind, palette, rng, halfX, halfZ);
  return new MeshBuilder().merge(model, place(def.x, y, def.z, yaw, propScale(def)));
}

/**
 * Marked minefields are tilled, wavy ground with a stake at each corner (01 §5); unmarked ones show
 * nothing at all. `stakes` stand up and belong with the props; `paint` lies flat and takes no outline.
 */
export function groundMarks(
  def: MissionDef, palette: Palette, rng: Pcg32, groundY: (x: number, z: number) => number,
): { stakes: MeshBuilder; paint: MeshBuilder } {
  const stakes = new MeshBuilder();
  const paint = new MeshBuilder();
  for (const field of def.mineFields) {
    if (!field.marked) continue;
    const tell = mineFieldTell(palette, rng.fork(`field${field.id}`), field.rect, groundY);
    paint.merge(tell.furrows, place());
    stakes.merge(tell.stakes, place());
  }
  const { x, z } = def.pickup;
  paint.merge(pickupMark(palette, rng.fork('pickup')), place(x, groundY(x, z) + 0.04, z));
  return { stakes, paint };
}
